import React, { useCallback, useMemo, useState } from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { Graph_QL, useSystem } from "../../Services";
import { useAppSelector } from "../../Store";
import NewButton, { NewButtonList } from "./newButton";

interface UploadImageProps {
  onClose: () => void,
  systemId: number
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (err) => reject(err);
  });
};

const MAX_FILE_SIZE = 500 * 1024;

export default function UploadImage({onClose, systemId}: UploadImageProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFileName, setDroppedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string>("")
  const [base64Data, setBase64Data] = useState<string|undefined>();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { maintainSystemImage } = useSystem(connection);

  // #region | Handlers
  // Handle Drag Over
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle Drag Leave
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle validation
  const validateAndSetFile = useCallback(async (file: File | null, source: "drop" | "input") => {
    if (!file) return;
    const array = new Uint8Array(await file.arrayBuffer());
    const start = Array.from(array.subarray(0,2)).map((value) => value.toString(16));
    const end = Array.from(array.subarray(-2)).map((value) => value.toString(16));
    // magic number for jpeg are ff d8 for start and ff d9 for end.
    if (file.type !== "image/jpeg" || (start[0] !== "ff" && start[1] !== "d8" && end[0] !== "ff" && end[1] !== "d9" )) {
      setError(source === "drop" ? "Please drop a JPEG file" : "Please select a JPEG file");
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }
  
    if (file.size > MAX_FILE_SIZE) {
      setError("File is too large (max 500 kB)");
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }
  
    try {
      const result = await fileToBase64(file);
      setBase64Data(result);
    } catch (error) {
      setError("Error processing file");
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }

    setDroppedFileName(file.name);
  }, []);

  // Handle Drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFile(e.dataTransfer.files[0], "drop");
  }, [validateAndSetFile]);

  // Handle File Input
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetFile(e.target.files?.[0] || null, "input");
  }, [validateAndSetFile]);
  // #endregion

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        width: "350px",
        height: "250px",
        borderRadius: "10px",
        borderWidth: "3px",
        borderStyle: "dashed",
        borderColor: isDragging ? colors.greyLightTertiary : colors.blueDarkSecondary,
        boxSizing: "border-box",
        margin: "0px auto",
        transition: "border-color 0.3s ease",
      }}
    >
      {!droppedFileName ? (<div style={{opacity: isDragging ? "0.2" : undefined, height: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", transition: "opacity 0.3s ease"}}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <glyphs.Upload size="40px"/>
          <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize3, ...fonts.paragraph }}>
            Drag and drop your blueprint here!
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", position: "relative" }}>
          <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.paragraph }}>
            Or browse it from your computer:
          </p>
          <NewButton
            content="Choose file"
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/jpeg";
              input.onchange = handleFileInput as unknown as (this: GlobalEventHandlers, ev: Event) => any;
              input.click();
            }}
          />
          <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.header, position: "absolute", bottom: "-23px" }}> {error} </p>
        </div>
      </div> ) : ( <div style={{height: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px"}}>
        <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.paragraph }}> {droppedFileName} </p>
        <NewButtonList
          contents={["save", "choose again"]}
          onClick={(i) => {
            if (i === 0) {
              maintainSystemImage(systemId, base64Data);
              onClose(); 
            }
            if (i === 1) setDroppedFileName(null);
          }}
          overrides={{
            baseStyles: {width: "130px"}
          }}
        />
      </div> )}
    </div>
  );
};
